import { useState } from "react";
import useSWR from "swr";
import { Table, Button, Group, Stack, Text, Checkbox, Modal, TextInput, ActionIcon, Alert } from "@mantine/core";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { useFetch, getSemesters, createSemester, updateSemester, deleteSemester, Semester } from "../../../shared/api";
import { ErrorDisplay, Loading, formatDate } from "../../../shared/utils";
import { FilterBar, useFilterDraft } from "../../../shared/components/FilterBar";

interface FormState { title: string; starts_on: string; ends_on: string; is_active: boolean }
const EMPTY: FormState = { title: "", starts_on: "", ends_on: "", is_active: false };

export function SemestersTab() {
  const { fetch } = useFetch();
  // The full list drives the single-active-semester rule; the table shows the server-filtered one.
  const { data, error, isLoading, mutate: mutateAll } = useSWR("admin/semesters", () => getSemesters({ fetch }));
  const [applied, setApplied] = useState({ search: "" });
  const { draft, patch, dirty } = useFilterDraft(applied);
  const { data: shown, mutate: mutateShown } = useSWR(["admin/semesters/filtered", applied.search], () => getSemesters({ search: applied.search.trim(), fetch }));
  const mutate = () => Promise.all([mutateAll(), mutateShown()]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Semester | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);

  const openForm = (s?: Semester) => {
    setEditing(s || null);
    setForm(s ? { title: s.title, starts_on: s.starts_on ?? "", ends_on: s.ends_on ?? "", is_active: s.is_active } : EMPTY);
    setSaveError(null); setModalOpen(true);
  };

  async function dedupeActive(keepId: string) {
    for (const o of (data ?? []).filter(x => x.id !== keepId && x.is_active)) {
      await updateSemester({ id: o.id, patch: { is_active: false }, fetch });
    }
  }

  async function save() {
    setSaving(true); setSaveError(null);
    try {
      const patch = { title: form.title, starts_on: form.starts_on || null, ends_on: form.ends_on || null, is_active: form.is_active || (!editing && (data?.length ?? 0) === 0) };
      if (editing) {
        await updateSemester({ id: editing.id, patch, fetch });
        if (patch.is_active) await dedupeActive(editing.id);
      } else {
        const { ids } = await createSemester({ semester: { id: crypto.randomUUID().replace(/-/g, ""), ...patch }, fetch });
        if (patch.is_active && ids[0]) await dedupeActive(ids[0]);
      }
      setModalOpen(false); await mutate();
    } catch (e: any) { setSaveError(e?.message ?? String(e)); }
    finally { setSaving(false); }
  }

  async function setActive(s: Semester) {
    if (s.is_active) return;
    setOpError(null);
    try {
      await updateSemester({ id: s.id, patch: { is_active: true }, fetch });
      await dedupeActive(s.id); await mutate();
    } catch (e: any) { setOpError(e?.message ?? String(e)); }
  }

  async function remove(s: Semester) {
    if (!window.confirm(`Удалить семестр «${s.title}»?`)) return;
    setOpError(null);
    try {
      await deleteSemester({ id: s.id, fetch });
      const rem = (data ?? []).filter(x => x.id !== s.id);
      if (s.is_active && rem.length > 0 && !rem.some(x => x.is_active)) {
        await updateSemester({ id: rem[rem.length - 1].id, patch: { is_active: true }, fetch });
      }
      await mutate();
    } catch (e: any) { setOpError(e?.message ?? String(e)); }
  }

  if (error) return <ErrorDisplay error={error} />;
  if (isLoading || !data) return <Loading />;

  return (
    <Stack>
      <Group justify="space-between"><Text fw={500}>Всего: {data.length}</Text><Button leftSection={<Plus size={16} />} onClick={() => openForm()}>Добавить семестр</Button></Group>
      <FilterBar dirty={dirty} onApply={() => setApplied(draft)} onReset={() => setApplied({ search: "" })}>
        <TextInput placeholder="Поиск по названию" leftSection={<Search size={16} />} value={draft.search} onChange={e => patch({ search: e.target.value })} style={{ flex: 1 }} />
      </FilterBar>
      {opError && <Alert color="red">{opError}</Alert>}
      <Table highlightOnHover>
        <Table.Thead><Table.Tr><Table.Th w={90}>Активный</Table.Th><Table.Th>Название</Table.Th><Table.Th>Начало</Table.Th><Table.Th>Конец</Table.Th><Table.Th w={90} /></Table.Tr></Table.Thead>
        <Table.Tbody>
          {(shown ?? []).map(s => (
            <Table.Tr key={s.id}>
              <Table.Td><Checkbox checked={s.is_active} onChange={() => setActive(s)} aria-label="Сделать активным" /></Table.Td>
              <Table.Td>{s.title}</Table.Td>
              <Table.Td>{s.starts_on ? formatDate(s.starts_on) : "—"}</Table.Td>
              <Table.Td>{s.ends_on ? formatDate(s.ends_on) : "—"}</Table.Td>
              <Table.Td>
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <ActionIcon variant="subtle" color="gray" onClick={() => openForm(s)}><Pencil size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => remove(s)}><Trash2 size={16} /></ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {shown && shown.length === 0 && <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="md">{data.length === 0 ? "Семестров пока нет" : "Ничего не найдено"}</Text></Table.Td></Table.Tr>}
        </Table.Tbody>
      </Table>
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Редактировать семестр" : "Новый семестр"}>
        <Stack>
          <TextInput label="Название" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <Group grow><TextInput type="date" label="Начало" value={form.starts_on} onChange={e => setForm(f => ({ ...f, starts_on: e.target.value }))} /><TextInput type="date" label="Конец" value={form.ends_on} onChange={e => setForm(f => ({ ...f, ends_on: e.target.value }))} /></Group>
          <Checkbox label="Активный" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          {saveError && <Alert color="red">{saveError}</Alert>}
          <Group justify="flex-end"><Button variant="default" onClick={() => setModalOpen(false)}>Отмена</Button><Button onClick={save} loading={saving} disabled={!form.title}>Сохранить</Button></Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
